'use client';

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import type { ProductIndexData } from '@eliadmin/core/src/api/products/types/product-types';

import { fetchBlogs } from '@eliadmin/core/src/api/blogs/blogs-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchCategoriesSearch, fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { fetchPrinterModels } from '@eliadmin/core/src/components/nt/shared/printer-search-model';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useDebounce } from '@eliadmin/core/src/hooks/use-debounce';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cleanHref, stripTags } from '@eliadmin/core/src/utils/nt/text';

/** Like the core search hook: queries from the second character. */
export const MIN_QUERY = 2;
/** Typing pause before a query (core waits 600 ms; shorter feels immediate, still one request). */
const DELAY = 300;
/**
 * Results per group – Figma 185:14902 lists three printers and five products; categories and
 * articles keep the core header search count (5) and a short list (2) under the printers.
 */
const RESULTS = { printers: 3, categories: 5, articles: 2, products: 5 } as const;

/** Printer model, category or article of the panel: name and link. */
export type SearchLink = { id: number | string; name: string; href: string };

export type HeaderSearchResults = {
  /** „Tlačiarne“ – printer models whose name matches. */
  printers: SearchLink[];
  /** „Kategórie“ – the core category search without the printers above. */
  categories: SearchLink[];
  /** „Poradňa“ – blog articles (core blog search). */
  articles: SearchLink[];
  products: ProductIndexData[];
  /** All matching products (`meta.total`) – „Produkty ({pocet})“. */
  total: number;
};

type SearchRequest = { locale: string; authToken?: string; search: string; signal?: AbortSignal };

const toLink = (entry: { id: number | string; name: string }, href: string): SearchLink => ({
  id: entry.id,
  name: stripTags(entry.name),
  href,
});

/** „Tlačiarne“ – the printer models of the homepage printer search (`printer-search-model.tsx`). */
async function fetchPrinters(
  { locale, search, signal }: SearchRequest,
  brandIds: (number | string)[],
) {
  const models = await fetchPrinterModels(locale, brandIds, search, signal, RESULTS.printers);
  return models.map(model => toLink(model, cleanHref(model.slug)));
}

/** The core category search (typed as products by the core; only name and slug are read). */
async function fetchCategories({ locale, authToken, search }: SearchRequest) {
  const { data } = await fetchCategoriesSearch({
    locale,
    authToken,
    query: { search, results: RESULTS.categories },
  });
  return data.map(category => toLink(category, cleanHref(category.slug)));
}

async function fetchArticles({ locale, authToken, search }: SearchRequest) {
  const { data } = await fetchBlogs({
    locale,
    authToken,
    query: { search, results: RESULTS.articles },
  });
  return data.map(blog => toLink(blog, ROUTES.BLOG_DETAIL({ slug: blog.slug })));
}

/**
 * The header search for a debounced query: printer models, categories, articles and products in
 * parallel. A failed group is left empty so the others still show; the previous results stay while
 * the next query loads, so the panel does not flash „nothing found“.
 */
export function useHeaderSearch(
  query: string,
  printerBrandIds: (number | string)[],
  /** `products: false` – the search page lists the products itself, the preview is not needed. */
  { products: withProducts = true }: { products?: boolean } = {},
) {
  const locale = useLocaleStore(state => state.currentLocale);
  const { me, isInitialized } = useCustomerStore();
  const authToken = me?.authToken;
  const debounced = useDebounce(query, DELAY);

  const { data, isFetching } = useQuery({
    queryKey: [
      CacheKey.PRODUCTS,
      'header-search',
      locale,
      authToken ?? null,
      debounced,
      withProducts,
    ],
    queryFn: async ({ signal }): Promise<HeaderSearchResults> => {
      const request = { locale, authToken, search: debounced, signal };
      const [printers, categories, articles, products] = await Promise.all([
        fetchPrinters(request, printerBrandIds).catch(() => []),
        fetchCategories(request).catch(() => []),
        fetchArticles(request).catch(() => []),
        withProducts
          ? fetchProducts({
              locale,
              authToken,
              signal,
              query: { search: debounced, results: RESULTS.products },
            }).catch(() => null)
          : null,
      ]);
      const printerHrefs = new Set(printers.map(printer => printer.href));
      return {
        printers,
        categories: categories.filter(
          (category, index) =>
            !printerHrefs.has(category.href) &&
            categories.findIndex(other => other.href === category.href) === index,
        ),
        articles,
        products: products?.data ?? [],
        total: products?.meta.total ?? products?.data.length ?? 0,
      };
    },
    enabled: isInitialized && debounced.length >= MIN_QUERY,
    placeholderData: keepPreviousData,
  });

  return { results: data, query: debounced, isPending: query !== debounced || isFetching };
}
